// Namespace import so it compiles both in apps without esModuleInterop (most backends)
// and in stork-bff/stork-app. Only use its members (moment.utc, moment.tz), never call
// moment() directly: a namespace import is not callable in the frontend bundle.
import * as moment from 'moment-timezone';
import { ICellMapping, IParserSpec, IValueMap } from '../../interfaces';

/**
 * Cell-parser engine for the trips importer templating system.
 *
 * A parser decomposes one raw cell into named parts (`Record<string, ParsedValue>`);
 * a mapping's `outputs` then route those parts to N canonical target fields. Routing
 * one part or many is the same code path — this is the universal currency the future
 * checkpoint reshape / extra parser types compose onto.
 */
export type ParsedValue = string | number | Date;

export interface Parser {
    /** Decompose a raw cell into named parts. Return null if it can't parse. */
    parse(raw: unknown): Record<string, ParsedValue> | null;
}

/** Import-level inputs for parsers that don't depend only on the cell. */
export interface ParserContext {
    /** IANA timezone of the importing user; relative dates are computed on its calendar. */
    timezone?: string;
    /** Reference instant for relative dates. Defaults to the current time. */
    now?: Date;
}

/**
 * Unwraps the exceljs cell value shapes into the plain value a parser expects:
 * formulas yield their cached result, rich text and hyperlinks their text, and
 * error cells (#N/A, #REF!...) nothing. A formula without a cached result (file
 * written by a tool that doesn't calculate) yields nothing as well.
 */
export function normalizeCellValue(raw: unknown): unknown {
    if (raw === null || raw === undefined || raw instanceof Date || typeof raw !== 'object') return raw;

    const cell = raw as Record<string, unknown>;
    if ('error' in cell) return null;
    if ('formula' in cell || 'sharedFormula' in cell) return normalizeCellValue(cell['result']);
    if (Array.isArray(cell['richText']))
        return cell['richText'].map((run: { text?: string }) => run.text ?? '').join('');
    if ('hyperlink' in cell) return normalizeCellValue(cell['text']);
    return raw;
}

class DateTimeParser implements Parser {
    constructor(private readonly format: string) {}

    parse(raw: unknown): Record<string, ParsedValue> | null {
        // exceljs returns date cells as native Date built in UTC — keep it; the wall
        // clock is recovered with moment.utc downstream (see applyCellMapping).
        if (raw instanceof Date) return { value: raw };
        if (raw === null || raw === undefined) return null;
        const text = String(raw).trim();
        if (!text) return null;
        // Parse the wall-clock as UTC-naive (no server-tz shift). The downstream
        // createBaseTrip reinterprets this wall clock in the user's timezone.
        const dt = moment.utc(text, this.format, true); // strict parse
        return dt.isValid() ? { value: dt.toDate() } : null;
    }
}

class RawParser implements Parser {
    parse(raw: unknown): Record<string, ParsedValue> | null {
        if (raw === null || raw === undefined) return null;
        return { value: raw instanceof Date ? raw : String(raw).trim() };
    }
}

class RegexParser implements Parser {
    private readonly re: RegExp;

    constructor(pattern: string, flags?: string) {
        this.re = new RegExp(pattern, flags);
    }

    parse(raw: unknown): Record<string, ParsedValue> | null {
        if (raw === null || raw === undefined) return null;
        const text = String(raw).trim();
        const match = this.re.exec(text);
        if (!match) return null;

        if (match.groups && Object.keys(match.groups).length > 0) {
            return Object.fromEntries(Object.entries(match.groups).filter(([, v]) => v !== undefined)) as Record<
                string,
                string
            >;
        }

        const parts: Record<string, string> = { value: match[0] };
        match.slice(1).forEach((g, i) => {
            if (g !== undefined) parts[`group${i + 1}`] = g;
        });
        return parts;
    }
}

class ConstantParser implements Parser {
    constructor(private readonly value: string) {}

    parse(): Record<string, ParsedValue> {
        return { value: this.value };
    }
}

class RelativeDateParser implements Parser {
    constructor(
        private readonly days: number,
        private readonly time: string,
        private readonly context: ParserContext,
    ) {}

    parse(): Record<string, ParsedValue> | null {
        const now = this.context.now ?? new Date();
        const today = this.context.timezone ? moment.tz(now, this.context.timezone) : moment.utc(now);
        const day = today.add(this.days, 'day').format('YYYY-MM-DD');
        // Same UTC-naive wall-clock convention as DateTimeParser: downstream reinterprets
        // it in the user's timezone.
        const dt = moment.utc(`${day} ${this.time}`, 'YYYY-MM-DD HH:mm', true);
        return dt.isValid() ? { value: dt.toDate() } : null;
    }
}

export function makeParser(spec: IParserSpec, context: ParserContext = {}): Parser {
    switch (spec.type) {
        case 'datetime':
            return new DateTimeParser(spec.format);
        case 'regex':
            return new RegexParser(spec.pattern, spec.flags);
        case 'constant':
            return new ConstantParser(spec.value);
        case 'relativeDate':
            return new RelativeDateParser(spec.days, spec.time ?? '00:00', context);
        case 'raw':
        default:
            return new RawParser();
    }
}

export interface MappingError {
    /** Absent for source-less parsers (`constant`, `relativeDate`). */
    source?: string;
    target?: string;
    key?: string;
    reason: 'parse_failed' | 'missing_part' | 'unmapped_value';
    /** The value that had no entry in the output's value map (`unmapped_value`). */
    value?: string;
}

export interface AppliedMapping {
    /** canonical target field -> normalized string value */
    values: Record<string, string>;
    errors: MappingError[];
}

/**
 * Run a single cell mapping: parse the raw cell, then route named parts to targets,
 * reformatting Date parts per `output.format`. Returns normalized string values
 * (the canonical workbook is text-based) plus any per-cell errors.
 */
export function applyCellMapping(mapping: ICellMapping, raw: unknown, context: ParserContext = {}): AppliedMapping {
    const cell = normalizeCellValue(raw);

    // An empty cell is an absent value, not a malformed one: only required outputs complain.
    if (mapping.source && (cell === null || cell === undefined || String(cell).trim() === '')) {
        const errors: MappingError[] = mapping.outputs
            .filter((out) => out.required)
            .map((out) => ({ source: mapping.source, target: out.target, key: out.key, reason: 'missing_part' }));
        return { values: {}, errors };
    }

    const parts = makeParser(mapping.parser, context).parse(cell);
    if (!parts) {
        return { values: {}, errors: [{ source: mapping.source, reason: 'parse_failed' }] };
    }

    const values: Record<string, string> = {};
    const errors: MappingError[] = [];

    for (const out of mapping.outputs) {
        const part = parts[out.key];
        if (part === undefined) {
            if (out.required) {
                errors.push({ source: mapping.source, target: out.target, key: out.key, reason: 'missing_part' });
            }
            continue;
        }
        // Format Date parts in UTC so the wall clock matches what was parsed (and what
        // exceljs encoded), with no server-timezone shift.
        const formatted = out.format && part instanceof Date ? moment.utc(part).format(out.format) : part;
        const value = formatted instanceof Date ? moment.utc(formatted).toISOString() : String(formatted);
        if (!out.valueMap) {
            values[out.target] = value;
            continue;
        }
        const mapped = applyValueMap(out.valueMap, value);
        if (mapped === undefined) {
            errors.push({ source: mapping.source, target: out.target, reason: 'unmapped_value', value });
            continue;
        }
        values[out.target] = mapped;
    }

    return { values, errors };
}

/** Lowercase, no accents, trimmed: how value map entries are matched against file values. */
const normalizeForMatch = (text: string) =>
    text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

/**
 * Translates a value through a value map. Returns the mapped value, the value itself when
 * it isn't in the table and unmatched values are kept, or undefined when they are errors.
 */
export function applyValueMap(valueMap: IValueMap, value: string): string | undefined {
    const key = normalizeForMatch(value);
    const entry = valueMap.entries.find((candidate) => normalizeForMatch(candidate.from) === key);
    if (entry) return entry.to;
    return valueMap.unmatched === 'error' ? undefined : value;
}
